/**
 * Hook for downloading game CSV files with automatic state management
 *
 * This is the magnus-web equivalent of @aura-frontend/shared's useDownloadGameCsv.
 * It uses the local Supabase-based fetchGameCsv instead of the shared MSAL-based one.
 *
 * Features:
 * - Fetches CSV data from API
 * - Generates standardized filename
 * - Creates blob and triggers download
 * - Manages loading states
 * - Error handling with toast notifications
 */

import { Game } from "@aura-frontend/omnibus-az-client";
import { MutationStatus, useMutation } from "@tanstack/react-query";

import { downloadFile } from "@/utils/downloadFile";
import { fetchGameCsv } from "@/utils/fetchGameCsv";
import { generateGameCsvFilename } from "@/utils/generateGameCsvFilename";
import { trackEvent } from "@/utils/trackEvent";

type DownloadState = "idle" | "downloading" | "success" | "error";

interface UseDownloadGameCsvOptions {
  onError?: (message: string) => void;
}

interface UseDownloadGameCsvReturn {
  /**
   * Trigger CSV download for a game
   * @param game - The game to download CSV for
   */
  downloadCsv: (game: Game) => void;

  /**
   * Current download state
   */
  downloadState: DownloadState;

  /**
   * Whether a download is currently in progress
   */
  isDownloading: boolean;

  /**
   * ID of the game currently being downloaded (if any)
   */
  downloadingGameId: string | null;
}

const DOWNLOAD_STATE: Record<MutationStatus, DownloadState> = {
  idle: "idle",
  pending: "downloading",
  success: "success",
  error: "error",
};

export function useDownloadGameCsv({
  onError,
}: UseDownloadGameCsvOptions = {}): UseDownloadGameCsvReturn {
  // Download from the click, not from an effect watching fetched data: the
  // effect re-ran on every re-render with a new onError and saved the file twice.
  const { mutate, status, variables } = useMutation({
    mutationFn: async (game: Game) => {
      if (!game.gameUuid) {
        throw new Error("Game has no id");
      }
      const csv = await fetchGameCsv(game.gameUuid);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadFile(blob, generateGameCsvFilename(game));
      trackEvent("csv_downloaded", { gameId: game.gameUuid });
    },
    onError: (error) => {
      console.error("Failed to download CSV:", error);
      onError?.("Failed to download CSV file. Please try again.");
    },
  });

  return {
    downloadCsv: mutate,
    downloadState: DOWNLOAD_STATE[status],
    isDownloading: status === "pending",
    downloadingGameId:
      status === "pending" ? (variables?.gameUuid ?? null) : null,
  };
}
